import AppApi from '../app/redux/api'
import {firebaseMessagingService} from './firebaseMessaging'
import {tokenLogger} from '../Utils/ccpLogger'

// Expected foreground poll cadence (kept in sync with usePushNotifications poll interval).
// A measured gap meaningfully larger than this is evidence the browser throttled/froze the
// timer (background tab, discarded tab, machine sleep) — the hypothesis we're trying to prove.
const EXPECTED_POLL_INTERVAL_MS = 2 * 60 * 1000
const POLL_GAP_ANOMALY_MS = 3 * 60 * 1000 // 1.5x expected

const visibilitySnapshot = () => ({
  visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
  hasFocus: typeof document !== 'undefined' && typeof document.hasFocus === 'function' ? document.hasFocus() : null,
  online: typeof navigator !== 'undefined' ? navigator.onLine : null,
  permission:
    typeof window !== 'undefined' && typeof window.Notification !== 'undefined'
      ? window.Notification.permission
      : 'unknown',
})

export interface DeviceRegistrationPayload {
  deviceTokenId: string
  deviceType: string
  deviceName: string
  deviceModel: string
}

export interface DeviceRegistrationResponse {
  status: string
  message: string
  data?: any
}

export const pushNotificationApi = AppApi.injectEndpoints({
  endpoints: (builder) => ({
    registerDevice: builder.mutation<DeviceRegistrationResponse, DeviceRegistrationPayload>({
      query: (deviceData) => ({
        url: '/PushNotification/register-device',
        method: 'POST',
        body: deviceData,
      }),
    }),
    unregisterDevice: builder.mutation<DeviceRegistrationResponse, string>({
      query: (deviceTokenId) => ({
        url: '/PushNotification/unregister-device',
        method: 'POST',
        body: deviceTokenId,
        headers: {
          'Content-Type': 'text/plain',
        },
      }),
    }),
    updateDeviceToken: builder.mutation<DeviceRegistrationResponse, string>({
      query: (deviceTokenId) => ({
        url: '/PushNotification/update-device-token',
        method: 'POST',
        body: deviceTokenId,
        headers: {
          'Content-Type': 'text/plain',
        },
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useRegisterDeviceMutation, 
  useUnregisterDeviceMutation, 
  useUpdateDeviceTokenMutation,
} = pushNotificationApi

/**
 * Service class to handle push notification operations
 */
export class PushNotificationService {
  private static instance: PushNotificationService
  private isRegistered: boolean = false
  private currentDeviceToken: string | null = null
  private isRegistering: boolean = false // Keep to prevent concurrent calls
  private lastRegistrationTime: number = 0 // Change to track time instead of token
  private currentSessionRegistered: boolean = false // Track if registered in current session
  private lastPollTime: number = 0 // Timestamp of the previous pollDeviceToken() call (for gap measurement)
  private pollCount: number = 0 // Number of poll ticks observed this session

  private constructor() {
    // Initialize state from localStorage on startup
    this.initializeFromStorage()
  }

  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService()
    }
    return PushNotificationService.instance
  }

  /**
   * Initialize service state from localStorage
   */
  private initializeFromStorage(): void {
    try {
      const storedToken = localStorage.getItem('fcm_device_token')
      if (storedToken) {
        this.currentDeviceToken = storedToken
        // Don't set isRegistered=true here - force registration on every login
      }
    } catch (error) {
      console.error('Error initializing from localStorage:', error)
    }
  }

  /**
   * Reset session state (call this on logout)
   */
  public resetSession(): void {
    this.currentSessionRegistered = false
    this.isRegistered = false
  }

  /**
   * Initialize push notifications and register device
   */
  public async initializeAndRegister(): Promise<boolean> {
    try {
      // Prevent concurrent registrations
      if (this.isRegistering) {
        console.log('🟧 PushNotificationService: Already registering, skipping')
        return false
      }

      this.isRegistering = true
      this.lastRegistrationTime = Date.now()

      // Initialize Firebase messaging with retry (up to 3 attempts, 1s delay)
      let token: string | null = null
      for (let attempt = 1; attempt <= 3; attempt++) {
        console.log(`🔄 PushNotificationService: Token attempt ${attempt}/3`)
        token = await firebaseMessagingService.initialize()
        if (token) {
          console.log(`✅ PushNotificationService: Token obtained on attempt ${attempt}`)
          break
        }
        if (attempt < 3) {
          console.log('⏳ PushNotificationService: Token null, retrying in 1s...')
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      if (!token) {
        console.error('❌ PushNotificationService: Failed to get token after 3 attempts')
        tokenLogger.warn('🔔 initializeAndRegister: no FCM token after 3 attempts — registration aborted', {
          agentEmail: this.getAgentEmail(),
          ...visibilitySnapshot(),
        })
        this.isRegistering = false
        return false
      }

      // Get device information
      const deviceInfo = firebaseMessagingService.getDeviceInfo()

      // Prepare registration payload
      const registrationPayload: DeviceRegistrationPayload = {
        deviceTokenId: token,
        deviceType: deviceInfo.deviceType,
        deviceName: deviceInfo.deviceName,
        deviceModel: deviceInfo.deviceModel,
      }

      // Always register device with backend on login
      const success = await this.registerDeviceWithBackend(registrationPayload)

      if (success) {
        this.isRegistered = true
        this.currentDeviceToken = token
        this.currentSessionRegistered = true

        // Store token in localStorage for future reference
        localStorage.setItem('fcm_device_token', token)
        console.log('✅ PushNotificationService: Device registered successfully')
        tokenLogger.info('🔔 initializeAndRegister: device registered successfully', {
          agentEmail: this.getAgentEmail(),
          deviceType: deviceInfo.deviceType,
          deviceModel: deviceInfo.deviceModel,
        })
      } else {
        console.error('❌ PushNotificationService: Backend registration failed')
      }

      this.isRegistering = false
      return success
    } catch (error) {
      console.error('💥 PushNotificationService: Error initializing push notifications:', error)
      tokenLogger.error('🔔 initializeAndRegister: threw while initializing push notifications', {
        agentEmail: this.getAgentEmail(),
        error: error instanceof Error ? error.message : String(error),
        ...visibilitySnapshot(),
      })
      this.isRegistering = false
      return false
    }
  }

  /**
   * Register device with backend using RTK Query
   */
  private async registerDeviceWithBackend(payload: DeviceRegistrationPayload): Promise<boolean> {
    try {
      // We need to manually call the API since we're outside of a React component
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/PushNotification/register-device`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
          body: JSON.stringify(payload),
        }
      )

      const result = await response.json()

      if (response.ok && result.status === 'S') {
        return true
      } else {
        console.error('❌ PushNotificationService: Device registration failed:', result)
        tokenLogger.warn('🔔 register-device call rejected by backend', {
          agentEmail: this.getAgentEmail(),
          httpStatus: response.status,
          backendStatus: result?.status,
          backendMessage: result?.message ?? result?.text,
        })
        return false
      }
    } catch (error) {
      console.error('💥 PushNotificationService: Error registering device:', error)
      tokenLogger.error('🔔 register-device call threw (network/exception)', {
        agentEmail: this.getAgentEmail(),
        error: error instanceof Error ? error.message : String(error),
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      })
      return false
    }
  }

  /**
   * Unregister device from push notifications
   */
  public async unregisterDevice(): Promise<boolean> {
    try {
      const token = this.currentDeviceToken || localStorage.getItem('fcm_device_token')

      if (!token) {
        return true // Consider it successful if no token exists
      }

      // Unregister from backend
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/PushNotification/unregister-device`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
          body: token,
        }
      )

      const result = await response.json()

      if (response.ok) {
        // Clear all state
        this.isRegistered = false
        this.currentDeviceToken = null
        this.lastRegistrationTime = 0 // Reset registration time
        this.currentSessionRegistered = false // Reset session registered flag
        this.isRegistering = false // Clear any registration in progress flag

        // Remove token from localStorage
        localStorage.removeItem('fcm_device_token')

        tokenLogger.info('🔔 unregister-device: device unregistered successfully', {
          agentEmail: this.getAgentEmail(),
        })
        return true
      } else {
        console.error('Device unregistration failed:', result)
        tokenLogger.warn('🔔 unregister-device call rejected by backend', {
          agentEmail: this.getAgentEmail(),
          httpStatus: response.status,
          backendStatus: result?.status,
          backendMessage: result?.message ?? result?.text,
        })
        return false
      }
    } catch (error) {
      console.error('Error unregistering device:', error)
      tokenLogger.error('🔔 unregister-device call threw (network/exception)', {
        agentEmail: this.getAgentEmail(),
        error: error instanceof Error ? error.message : String(error),
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      })
      return false
    }
  }

  /**
   * Get auth token from local storage (using existing auth helpers)
   */
  private getAuthToken(): string | null {
    try {
      const authData = localStorage.getItem('kt-auth-react-v')
      if (authData) {
        const parsedAuth = JSON.parse(authData)
        return parsedAuth.token || null
      }
      return null
    } catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  }

  /**
   * Get the logged-in agent's email from local storage so token logs can be
   * filtered per-user in Sentry. Read directly from auth (rather than relying on
   * the shared log context) because device-token logs can fire before the CCP
   * agent initializes and sets that context.
   */
  private getAgentEmail(): string | null {
    try {
      const authData = localStorage.getItem('kt-auth-react-v')
      if (authData) {
        const parsedAuth = JSON.parse(authData)
        return parsedAuth.email || null
      }
      return null
    } catch (error) {
      console.error('Error getting agent email:', error)
      return null
    }
  }

  /**
   * Check if device is currently registered
   */
  public isDeviceRegistered(): boolean {
    return this.isRegistered
  }

  /**
   * Get current device token
   */
  public getCurrentDeviceToken(): string | null {
    return this.currentDeviceToken || localStorage.getItem('fcm_device_token')
  }

  /**
   * Re-register device if token changes
   */
  public async refreshRegistration(): Promise<boolean> {
    try {
      // Get fresh token
      const newToken = await firebaseMessagingService.getCurrentToken()

      if (newToken === null) {
        // Real failure: the SDK could not produce any token at all (permission
        // revoked, IndexedDB issue, push subscription invalidated, etc). This must
        // NOT be reported as success - previously it fell through to the
        // "no change needed" return below, making this failure mode invisible
        // until the interpreter happened to log out and back in.
        console.error(
          '🟥 PushNotificationService: getCurrentToken() returned null — push capability may be lost'
        )
        tokenLogger.warn('🔔 refreshRegistration: getCurrentToken() returned null — push capability lost', {
          agentEmail: this.getAgentEmail(),
          ...visibilitySnapshot(),
        })
        return false
      }

      if (newToken !== this.currentDeviceToken) {
        // Unregister old token if exists
        if (this.currentDeviceToken) {
          await this.unregisterDevice()
        }

        // Register with new token
        const deviceInfo = firebaseMessagingService.getDeviceInfo()
        const registrationPayload: DeviceRegistrationPayload = {
          deviceTokenId: newToken,
          deviceType: deviceInfo.deviceType,
          deviceName: deviceInfo.deviceName,
          deviceModel: deviceInfo.deviceModel,
        }

        const success = await this.registerDeviceWithBackend(registrationPayload)

        if (success) {
          this.currentDeviceToken = newToken
          this.isRegistered = true
          localStorage.setItem('fcm_device_token', newToken)
          tokenLogger.info('🔔 refreshRegistration: token changed — re-registered successfully', {
            agentEmail: this.getAgentEmail(),
          })
        } else {
          tokenLogger.warn('🔔 refreshRegistration: token changed but re-registration failed', {
            agentEmail: this.getAgentEmail(),
          })
        }

        return success
      }

      return true // No change needed
    } catch (error) {
      console.error('Error refreshing registration:', error)
      tokenLogger.error('🔔 refreshRegistration: threw while refreshing registration', {
        agentEmail: this.getAgentEmail(),
        error: error instanceof Error ? error.message : String(error),
        ...visibilitySnapshot(),
      })
      return false
    }
  }

  /**
   * User-initiated recovery from the "Force Update Token" modal.
   * Prefer a full FCM/SW re-init so a dead service worker is actually recovered.
   * Fall back to re-POSTing the cached token (LastUsedAt heartbeat only) when live init fails.
   * On success, emits `aac-device-token-force-updated` so usePushNotifications can mark
   * isRegistered and restart the poll loop (the service flag alone does not).
   */
  public async forceUpdateToken(): Promise<boolean> {
    try {
      const liveOk = await this.initializeAndRegister()
      if (liveOk) {
        this.notifyForceUpdated()
        console.log('🟩 PushNotificationService: forceUpdateToken succeeded via FCM re-init')
        return true
      }

      const token = this.currentDeviceToken || localStorage.getItem('fcm_device_token')
      if (!token) {
        console.error('🟥 PushNotificationService: forceUpdateToken - no FCM token and no cache')
        return false
      }

      const deviceInfo = firebaseMessagingService.getDeviceInfo()
      const success = await this.registerDeviceWithBackend({
        deviceTokenId: token,
        deviceType: deviceInfo.deviceType,
        deviceName: deviceInfo.deviceName,
        deviceModel: deviceInfo.deviceModel,
      })

      if (success) {
        this.currentDeviceToken = token
        this.isRegistered = true
        localStorage.setItem('fcm_device_token', token)
        this.notifyForceUpdated()
        console.log('🟩 PushNotificationService: forceUpdateToken succeeded via cached-token heartbeat')
      } else {
        console.error('🟥 PushNotificationService: forceUpdateToken - backend update failed')
      }

      return success
    } catch (error) {
      console.error('🟥 PushNotificationService: Error in forceUpdateToken:', error)
      return false
    }
  }

  private notifyForceUpdated(): void {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('aac-device-token-force-updated'))
  }

  /**
   * Poll/update device token
   * Call this periodically to keep the device token marked as active
   * Uses the same registerDevice endpoint which updates LastUsedAt for existing tokens
   */
  public async pollDeviceToken(): Promise<boolean> {
    // Measure the real elapsed time since the previous tick. If the foreground
    // setInterval were reliable this is ~EXPECTED_POLL_INTERVAL_MS every time; a
    // large gap is direct evidence the timer was throttled/frozen (background tab,
    // tab discard, machine sleep) — i.e. LastUsedAt silently went stale.
    const now = Date.now()
    const gapMs = this.lastPollTime ? now - this.lastPollTime : null
    this.lastPollTime = now
    this.pollCount += 1
    const snapshot = visibilitySnapshot()

    if (gapMs !== null && gapMs > POLL_GAP_ANOMALY_MS) {
      tokenLogger.warn('Token poll gap anomaly — foreground timer did not fire on schedule', {
        agentEmail: this.getAgentEmail(),
        gapMs,
        gapSec: Math.round(gapMs / 1000),
        expectedSec: EXPECTED_POLL_INTERVAL_MS / 1000,
        pollCount: this.pollCount,
        ...snapshot,
      })
    }
    try {
      const token = this.currentDeviceToken || localStorage.getItem('fcm_device_token')

      if (!token) {
        console.log('🟧 PushNotificationService: No device token to poll')
        tokenLogger.warn('🔔 pollDeviceToken: no device token available to poll', {
          agentEmail: this.getAgentEmail(),
          pollCount: this.pollCount,
          ...snapshot,
        })
        return false
      }

      // Get device info
      const deviceInfo = firebaseMessagingService.getDeviceInfo()


      const result = await this.registerDeviceWithBackend({
        deviceTokenId: token,
        deviceType: deviceInfo.deviceType,
        deviceName: deviceInfo.deviceName,
        deviceModel: deviceInfo.deviceModel,
      })

      if (result) {
        console.log('🟩 PushNotificationService: Device token poll successful')
        tokenLogger.info('🔔 pollDeviceToken: device token poll successful', {
          agentEmail: this.getAgentEmail(),
          pollCount: this.pollCount,
        })
        return true
      } else {
        console.error('🟥 PushNotificationService: Device token poll failed')
        tokenLogger.warn('🔔 pollDeviceToken: device token poll failed', {
          agentEmail: this.getAgentEmail(),
          pollCount: this.pollCount,
          ...snapshot,
        })
        return false
      }
    } catch (error) {
      console.error('🟥 PushNotificationService: Error polling device token:', error)
      tokenLogger.error('🔔 pollDeviceToken: threw while polling device token', {
        agentEmail: this.getAgentEmail(),
        error: error instanceof Error ? error.message : String(error),
        pollCount: this.pollCount,
        ...snapshot,
      })
      return false
    }
  }
}

export const pushNotificationService = PushNotificationService.getInstance()